/**
 * NewWellButton — the "+ Nuevo pozo" affordance in the Well Explorer page-head,
 * rendered ONLY for a global system-admin. It opens a stylized `<Modal>` (house
 * rule: create/edit forms open in a modal, not an inline row) with the register-
 * create form: UWI + Nombre are required; operadora, campo, estado, latitud and
 * longitud are optional.
 *
 * On save it POSTs to Oxbo-M via `createWell`; on success it selects the new well
 * and drops into its record — making this the primary way an operator brings a
 * well into the register. A 409 surfaces "ya existe un pozo con ese UWI" and a
 * 403 "solo administradores" (the same admin gate the backend enforces).
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "@app/hooks";
import { selectWell } from "@app/slices/wellContextSlice";
import { useLocale } from "@hooks/useLocale";
import { Modal } from "@components/ui/Modal";
import { useCreateWellMutation, type WellCreate } from "@api/mApi";
import { STATUS_KEY } from "@features/wellRecord/wellLabels";
import type { WellStatus } from "@api/types";

/** The known well-status values offered in the create form's Estado dropdown. */
const STATUS_VALUES: WellStatus[] = ["drilling", "completion", "producing", "suspended"];

function statusOf(err: unknown): number | null {
  return err && typeof err === "object" && "status" in err
    ? ((err as { status: unknown }).status as number)
    : null;
}

/** Blank → null; a finite number → itself; anything else → null (invalid ignored). */
function coord(raw: string): number | null {
  const s = raw.trim();
  if (s === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

interface Draft {
  uwi: string;
  common_name: string;
  operator: string;
  field: string;
  status: string;
  lat: string;
  lon: string;
}

const EMPTY: Draft = {
  uwi: "",
  common_name: "",
  operator: "",
  field: "",
  status: "",
  lat: "",
  lon: "",
};

export function NewWellButton() {
  const { t } = useLocale();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [createWell, { isLoading, error, reset }] = useCreateWellMutation();

  const set = <K extends keyof Draft>(k: K, v: string) => setDraft((d) => ({ ...d, [k]: v }));

  const openModal = () => {
    setDraft(EMPTY);
    reset();
    setOpen(true);
  };
  const close = () => {
    setOpen(false);
    reset();
  };

  const canSave = draft.uwi.trim() !== "" && draft.common_name.trim() !== "" && !isLoading;

  async function submit() {
    const body: WellCreate = {
      uwi: draft.uwi.trim(),
      common_name: draft.common_name.trim(),
      operator: draft.operator.trim() || null,
      field: draft.field.trim() || null,
      status: draft.status || null,
      lat: coord(draft.lat),
      lon: coord(draft.lon),
    };
    try {
      const well = await createWell(body).unwrap();
      dispatch(selectWell(well.id));
      setOpen(false);
      navigate("/well");
    } catch {
      // Error surfaces in the modal footer; keep it open so input isn't lost.
    }
  }

  const code = statusOf(error);
  const footerNote = error
    ? code === 409
      ? t("wellCreate.duplicate")
      : code === 403
        ? t("wellCreate.forbidden")
        : t("wellCreate.error")
    : null;

  return (
    <>
      <button type="button" className="btn primary" onClick={openModal}>
        {t("wellCreate.button")}
      </button>

      <Modal
        open={open}
        title={t("wellCreate.title")}
        onClose={close}
        onConfirm={submit}
        busy={isLoading}
        canConfirm={canSave}
        confirmLabel={isLoading ? t("wellCreate.saving") : t("wellCreate.save")}
        cancelLabel={t("wellCreate.cancel")}
        footerNote={footerNote}
      >
        <div className="modal-grid">
          <div className="field">
            <label htmlFor="nw-uwi">{t("wellCreate.field.uwi")}</label>
            <input
              id="nw-uwi"
              type="text"
              value={draft.uwi}
              onChange={(e) => set("uwi", e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="nw-name">{t("wellCreate.field.commonName")}</label>
            <input
              id="nw-name"
              type="text"
              value={draft.common_name}
              onChange={(e) => set("common_name", e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="nw-operator">{t("wellCreate.field.operator")}</label>
            <input
              id="nw-operator"
              type="text"
              value={draft.operator}
              onChange={(e) => set("operator", e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="nw-field">{t("wellCreate.field.field")}</label>
            <input
              id="nw-field"
              type="text"
              value={draft.field}
              onChange={(e) => set("field", e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="nw-status">{t("wellCreate.field.status")}</label>
            <select
              id="nw-status"
              value={draft.status}
              onChange={(e) => set("status", e.target.value)}
            >
              <option value="">—</option>
              {STATUS_VALUES.map((s) => (
                <option key={s} value={s}>
                  {t(STATUS_KEY[s])}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="nw-lat">{t("wellCreate.field.lat")}</label>
            <input
              id="nw-lat"
              type="number"
              step="any"
              inputMode="decimal"
              value={draft.lat}
              onChange={(e) => set("lat", e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="nw-lon">{t("wellCreate.field.lon")}</label>
            <input
              id="nw-lon"
              type="number"
              step="any"
              inputMode="decimal"
              value={draft.lon}
              onChange={(e) => set("lon", e.target.value)}
            />
          </div>
        </div>
      </Modal>
    </>
  );
}
